import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
  Avatar,
  useMediaQuery,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { convertFromISO } from "../utils/dateValidation";
import {
  getPetDetail,
  getPetMedical,
  getPetMedication,
  getPetVaccination,
  getPetDeworming,
  getPetBloodTest
} from "../services/api";
import { useTranslation } from "react-i18next";

const style = (isMobile) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#fcf8f3",
  borderRadius: "20px",
  boxShadow: 24,
  p: isMobile ? 2 : 4,
  minWidth: isMobile ? "90vw" : 1400,
  maxWidth: "90vw",
  maxHeight: "95vh",
  overflow: "auto",
});

const tabStyles = (isActive, isMobile) => ({
  fontWeight: isActive ? "bold" : "normal",
  fontSize: isMobile ? (isActive ? 16 : 14) : (isActive ? 20 : 18),
  pb: 1,
  borderBottom: isActive ? "3px solid #222" : "none",
  cursor: "pointer",
  "&:hover": {
    color: isActive ? "#222" : "#666",
  },
});

export default function PetInfoModal({ open, onClose, pet }) {
  const isMobile = useMediaQuery("(max-width:850px)");
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(t('Basic')); // Default tab
  const [petData, setPetData] = useState(pet);
  const [loadingMedicalData, setLoadingMedicalData] = useState(false);

  useEffect(() => {
    setActiveTab(t('Basic'));
  }, [t]);

  // Fetch medical-related data when modal opens
  useEffect(() => {
    const fetchMedicalData = async () => {
      if (pet && pet._id && open) {
        setLoadingMedicalData(true);
        try {
          const [medicalRes, medicationRes, vaccineRes, dewormRes, bloodTestRes] = await Promise.all([
            getPetMedical(pet._id).catch(() => ({ data: { form: { medical: [] } } })),
            getPetMedication(pet._id).catch(() => ({ data: { form: { medication: [] } } })),
            getPetVaccination(pet._id).catch(() => ({ data: { form: { vaccineRecords: [] } } })),
            getPetDeworming(pet._id).catch(() => ({ data: { form: { deworm: [] } } })),
            getPetBloodTest(pet._id).catch(() => ({ data: { form: { blood_test: [] } } }))
          ]);

          setPetData({
            ...pet,
            medical: medicalRes.data.form.medical || [],
            medication: medicationRes.data.form.medication || [],
            vaccineRecords: vaccineRes.data.form.vaccineRecords || [],
            deworm: dewormRes.data.form.deworm || [],
            bloodTest: bloodTestRes.data.form.blood_test || []
          });
        } catch (error) {
          console.error('Error fetching medical data:', error);
          setPetData({
            ...pet,
            medical: [],
            medication: [],
            vaccineRecords: [],
            deworm: [],
            bloodTest: []
          });
        } finally {
          setLoadingMedicalData(false);
        }
      }
    };

    fetchMedicalData();
  }, [pet, open]);

  // Update petData when pet prop changes
  useEffect(() => {
    if (pet) {
      setPetData(pet);
    }
  }, [pet]);

  if (!pet) return null;

  const renderContent = () => {
    switch (activeTab) {
      case t('Basic'):
        return (
          <>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mt: 3, mb: 1, fontSize: isMobile ? 16 : 20 }}
            >
              {t('BasicInfo')}
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}
              >
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #e0e0e0" }}>
                    <th style={{ padding: "8px" }}></th>
                    <th style={{ padding: "8px" }}>{t('Name')}</th>
                    <th style={{ padding: "8px" }}>{t('DateofBirth')}</th>
                    <th style={{ padding: "8px" }}>{t('Gender')}</th>
                    <th style={{ padding: "8px" }}>{t('Neutered')}</th>
                    <th style={{ padding: "8px" }}>{t('Status')}</th>
                    <th style={{ padding: "8px" }}>{t('Species')}</th>
                    <th style={{ padding: "8px" }}>{t('breed')}</th>
                    <th style={{ padding: "8px" }}>{t('weight')}</th>
                    <th style={{ padding: "8px" }}>{t('Features')}</th>
                    <th style={{ padding: "8px" }}>{t('OtherInfo')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={11} style={{ height: 12 }}></td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "center" }}>
                      <Avatar
                        src={pet.breedimage?.[0] || pet.photo}
                        sx={{ width: 40, height: 40, mx: "auto" }}
                      />
                    </td>
                    <td style={{ textAlign: "center" }}>{pet.name}</td>
                    <td style={{ textAlign: "center" }}>
                      {convertFromISO(pet.birthday)}
                    </td>
                    <td style={{ textAlign: "center" }}>{pet.sex}</td>
                    <td style={{ textAlign: "center" }}>{pet.sterilization}</td>
                    <td style={{ textAlign: "center" }}>
                      <Chip
                        label={
                          pet.status === "遺失"
                            ? t("Lost")
                            : pet.status === "在家"
                              ? t("AtHome")
                              : pet.status
                        }
                        color={
                          pet.status === "遺失"
                            ? "error"
                            : pet.status === "在家"
                              ? "success"
                              : "info"
                        }
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: 14,
                          ml: 1,
                          px: 1.5,
                          background:
                            pet.status === "遺失"
                              ? "#FFEAEA"
                              : pet.status === "在家"
                                ? "#EAF6FF"
                                : "transparent",
                          color:
                            pet.status === "遺失"
                              ? "#F44336"
                              : pet.status === "在家"
                                ? "#2196F3"
                                : "#000",
                        }}
                        icon={
                          pet.status === "遺失" ? (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                bgcolor: "#F44336",
                                borderRadius: "50%",
                              }}
                            />
                          ) : pet.status === "在家" ? (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                bgcolor: "#2196F3",
                                borderRadius: "50%",
                              }}
                            />
                          ) : (
                            <Box />
                          )
                        }
                      />
                    </td>
                    <td style={{ textAlign: "center" }}>{pet.animal === "貓" ? t("cat") : pet.animal === "狗" ? t("dog") : t("other")}</td>
                    <td style={{ textAlign: "center" }}>{pet.breed}</td>
                    <td style={{ textAlign: "center" }}>{pet.weight}</td>
                    <td style={{ textAlign: "center" }}>{pet.features}</td>
                    <td style={{ textAlign: "center" }}>{pet.info}</td>
                  </tr>
                </tbody>
              </table>
            </Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mt: 8, mb: 2, fontSize: isMobile ? 16 : 20 }}
            >
              {t('ContactInfo')}
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}
              >
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #e0e0e0" }}>
                    <th style={{ padding: "8px", textAlign: "left" }}>{t('ContactPerson')}</th>
                    <th style={{ padding: "8px", textAlign: "left" }}>{t('ContactPhone')}</th>
                    <th style={{ padding: "8px", textAlign: "left" }}>{t('OtherContact')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={3} style={{ height: 12 }}></td>
                  </tr>
                  <tr>
                    <td style={{ padding: "8px", textAlign: "left" }}>{pet.owner}</td>
                    <td style={{ padding: "8px", textAlign: "left" }}>{pet.ownerContact1}</td>
                    <td style={{ padding: "8px", textAlign: "left" }}>{pet.ownerContact2}</td>
                  </tr>
                </tbody>
              </table>
            </Box>
          </>
        );
      case t('Details'):
        return (
          <>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mt: 3, mb: 1, fontSize: isMobile ? 16 : 20 }}
            >
              {t("DetailInfo")}
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse" }}
              >
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #e0e0e0" }}>
                    <th style={{ padding: "8px" }}>{t("ChipNo")}</th>
                    <th style={{ padding: "8px" }}>{t("PlaceOfBirth")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={11} style={{ height: 12 }}></td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "center" }}>{pet.chipId}</td>
                    <td style={{ textAlign: "center" }}>{pet.placeOfBirth}</td>
                  </tr>
                </tbody>
              </table>
            </Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mt: 8, mb: 2, fontSize: isMobile ? 16 : 20 }}
            >
              {t("TransferRecord")}
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}
              >
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #e0e0e0" }}>
                    <th style={{ padding: "15px" }}>{t("RegistrationDate")}</th>
                    <th style={{ padding: "15px" }}>{t("contactNumber")}</th>
                    <th style={{ padding: "8px" }}>{t("RegistrationLocation")}</th>
                    <th style={{ padding: "8px" }}>{t("Remarks")}</th>
                    <th style={{ padding: "8px" }}>{t("TransferOwner")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={11} style={{ height: 12 }}></td>
                  </tr>
                  {petData.transfer && petData.transfer.length > 0 ? (
                    petData.transfer.map((transfer) => (
                      <tr key={transfer._id}>
                        <td style={{ textAlign: "center" }}> {convertFromISO(transfer.regDate)} </td>
                        <td style={{ textAlign: "center" }}> {transfer.transferContact}</td>
                        <td style={{ textAlign: "center" }}>{transfer.regPlace} </td>
                        <td style={{ textAlign: "center" }}>{transfer.transferRemark}</td>
                        <td style={{ textAlign: "center" }}>{transfer.transferOwner}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ padding: "8px", textAlign: "center" }}>
                        {t("NoTransfer")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>

            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mt: 8, mb: 2, fontSize: isMobile ? 16 : 20 }}
            >
              {t("petMotherInfo")}
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}
              >
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #e0e0e0" }}>
                    <th style={{ padding: "8px" }}>{t("petMotherName")}</th>
                    <th style={{ padding: "8px" }}>{t("ChipNo")}</th>
                    <th style={{ padding: "8px" }}>{t("breed")}</th>
                    <th style={{ padding: "8px" }}>{t("PlaceOfBirth")}</th>
                    <th style={{ padding: "8px" }}>{t("DateofBirth")}</th>
                    <th style={{ padding: "8px" }}>{t("FetalInfo")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={3} style={{ height: 12 }}></td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "center" }}>{pet.motherName}</td>
                    <td style={{ textAlign: "center" }}>{pet.motherChip}</td>
                    <td style={{ textAlign: "center" }}>{pet.motherBreed}</td>
                    <td style={{ textAlign: "center" }}>{pet.motherPlaceOfBirth}</td>
                    <td style={{ textAlign: "center" }}>{convertFromISO(pet.motherDOB)}</td>
                    <td style={{ textAlign: "center" }}>{pet.motherParity}</td>
                  </tr>
                </tbody>
              </table>
            </Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mt: 8, mb: 2, fontSize: isMobile ? 16 : 20 }}
            >
              {t("petFatherInfo")}
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}
              >
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #e0e0e0" }}>
                    <th style={{ padding: "8px" }}>{t("petFatherName")}</th>
                    <th style={{ padding: "8px" }}>{t("ChipNo")}</th>
                    <th style={{ padding: "8px" }}>{t("breed")}</th>
                    <th style={{ padding: "8px" }}>{t("PlaceOfBirth")}</th>
                    <th style={{ padding: "8px" }}>{t("DateofBirth")}</th>
                    <th style={{ padding: "8px" }}>{t("FetalInfo")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={3} style={{ height: 12 }}></td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "center" }}>{pet.fatherName}</td>
                    <td style={{ textAlign: "center" }}>{pet.fatherChip}</td>
                    <td style={{ textAlign: "center" }}>{pet.fatherBreed}</td>
                    <td style={{ textAlign: "center" }}>{pet.fatherPlaceOfBirth}</td>
                    <td style={{ textAlign: "center" }}>{convertFromISO(pet.fatherDOB)}</td>
                    <td style={{ textAlign: "center" }}>{pet.fatherParity}</td>
                  </tr>
                </tbody>
              </table>
            </Box>
          </>
        );
      case t('Medical'):
        return (
          <>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mt: 3, mb: 1, fontSize: isMobile ? 16 : 20 }}
            >
              {t("MedicalRecords")}
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}
              >
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #e0e0e0" }}>
                    <th style={{ padding: "8px" }}>{t("MedicationDate")}</th>
                    <th style={{ padding: "8px" }}>{t("Clinic/HospitalLocation")}</th>
                    <th style={{ padding: "8px" }}>{t("DoctorName")}</th>
                    <th style={{ padding: "8px" }}>{t("DiagnosisResult")}</th>
                    <th style={{ padding: "8px" }}>{t("TreatmentPlan")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={11} style={{ height: 12 }}></td>
                  </tr>
                  {petData.medical && petData.medical.length > 0 ? (
                    petData.medical.map((medical) => (
                      <tr key={medical._id}>
                        <td style={{ textAlign: "center" }}> {convertFromISO(medical.medicalDate)} </td>
                        <td style={{ textAlign: "center" }}>{medical.medicalPlace} </td>
                        <td style={{ textAlign: "center" }}> {medical.medicalDoctor}</td>
                        <td style={{ textAlign: "center" }}>{medical.medicalResult}</td>
                        <td style={{ textAlign: "center" }}>{medical.medicalSolution}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ padding: "8px", textAlign: "center" }}>
                        {t("NoMedicalRecords")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </>
        );
      case t('Medication'):
        return (
          <>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mt: 3, mb: 1, fontSize: isMobile ? 16 : 20 }}
            >
              {t("MedicationRecords")}
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}
              >
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #e0e0e0" }}>
                    <th style={{ padding: "8px" }}>{t("MedicationDate")}</th>
                    <th style={{ padding: "8px" }}>{t("drugName")}</th>
                    <th style={{ padding: "8px" }}>{t("drugPurpose")}</th>
                    <th style={{ padding: "8px" }}>{t("drugMethod")}</th>
                    <th style={{ padding: "8px" }}>{t("allergy")}</th>
                    <th style={{ padding: "8px" }}>{t("Remarks")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={11} style={{ height: 12 }}></td>
                  </tr>
                  {petData.medication && petData.medication.length > 0 ? (
                    petData.medication.map((medication) => (
                      <tr key={medication._id}>
                        <td style={{ textAlign: "center" }}> {convertFromISO(medication.medicationDate)} </td>
                        <td style={{ textAlign: "center" }}>{medication.drugName} </td>
                        <td style={{ textAlign: "center" }}>{medication.drugPurpose}</td>
                        <td style={{ textAlign: "center" }}> {medication.drugMethod}</td>
                        <td style={{ textAlign: "center" }}>{medication.allergy ? "有" : "沒有"}</td>
                        <td style={{ textAlign: "center" }}>{medication.drugRemark}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center" }}>
                        {t("NoMedicationRecords")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </>
        );
      case t("Deworming"):
        return (
          <>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mt: 3, mb: 1, fontSize: isMobile ? 16 : 20 }}
            >
              {t("DewormingRecords")}
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}
              >
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #e0e0e0" }}>
                    <th style={{ padding: "4px" }}>{t("dewormingDate")}</th>
                    <th style={{ padding: "4px" }}>{t("vaccineBrand")}</th>
                    <th style={{ padding: "4px" }}>{t("vaccineType")}</th>
                    <th style={{ padding: "4px" }}>{t("typesOfInternalParasites")}</th>
                    <th style={{ padding: "4px" }}>{t("typesOfExternalParasites")}</th>
                    <th style={{ padding: "4px" }}>{t("frequency")}</th>
                    <th style={{ padding: "4px" }}>{t("nextDewormDate")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={11} style={{ height: 12 }}></td>
                  </tr>
                  {petData.deworm && petData.deworm.length > 0 ? (
                    petData.deworm.map((deworm) => (
                      <tr key={deworm._id}>
                        <td style={{ textAlign: "center" }}> {convertFromISO(deworm.date)} </td>
                        <td style={{ textAlign: "center" }}>{deworm.vaccineBrand} </td>
                        <td style={{ textAlign: "center" }}> {deworm.vaccineType}</td>
                        <td style={{ textAlign: "center" }}>{(deworm.typesOfInternalParasites || [])
                          .filter(Boolean)
                          .join(", ") || "-"}
                        </td>
                        <td style={{ textAlign: "center" }}>{(deworm.typesOfExternalParasites || [])
                          .filter(Boolean)
                          .join(", ") || "-"}
                        </td>
                        <td style={{ textAlign: "center" }}>{deworm.frequency}</td>
                        <td style={{ textAlign: "center" }}>{convertFromISO(deworm.nextDewormDate)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center" }}>
                        {t("NoDewormingRecords")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </>
        );
      case t("Vaccine"):
        return (
          <>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mt: 3, mb: 1, fontSize: isMobile ? 16 : 20 }}
            >
              {t("VaccineRecords")}
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}
              >
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #e0e0e0" }}>
                    <th style={{ padding: "8px" }}>{t("InocculationDate")}</th>
                    <th style={{ padding: "8px" }}>{t("vaccineName")}</th>
                    <th style={{ padding: "8px" }}>{t("vaccineNumber")}</th>
                    <th style={{ padding: "8px" }}>{t("vaccineTimes")}</th>
                    <th style={{ padding: "8px" }}>{t("vaccinePosition")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={11} style={{ height: 12 }}></td>
                  </tr>
                  {petData.vaccineRecords && petData.vaccineRecords.length > 0 ? (
                    petData.vaccineRecords.map((vaccine) => (
                      <tr key={vaccine._id}>
                        <td style={{ textAlign: "center" }}> {convertFromISO(vaccine.vaccineDate)} </td>
                        <td style={{ textAlign: "center" }}>{vaccine.vaccineName} </td>
                        <td style={{ textAlign: "center" }}> {vaccine.vaccineNumber}</td>
                        <td style={{ textAlign: "center" }}>{vaccine.vaccineTimes}</td>
                        <td style={{ textAlign: "center" }}>{vaccine.vaccinePosition}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center" }}>
                        {t("NoVaccineRecords")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style(isMobile)}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ fontSize: isMobile ? 22 : 32 }}
          >
            {pet.name}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        {/* Tabs */}
        <Box
          sx={{
            mt: 2,
            mb: 1,
            gap: isMobile ? "10px" : "30px",
            borderBottom: "1.5px solid #e0e0e0",
            display: "flex",
          }}
        >
          {[t('Basic'), t('Details'), t('Medical'), t('Medication'), t('Deworming'), t('Vaccine')].map((tab) => (
            <Box
              key={tab}
              sx={tabStyles(tab === activeTab, isMobile)}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </Box>
          ))}
        </Box>
        {renderContent()}
      </Box>
    </Modal>
  );
}